import '../../styles/cabinet-trial.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bar,
  Button,
  Help,
  Notice,
  Status,
  useToast,
  type StatusKind,
} from '@/components/kit/ds';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import {
  createCheckoutThunk,
  createPortalThunk,
  fetchBillingThunk,
} from '@/store/billingSlice';
import { useBillingProjectId } from '@/hooks/useBillingProjectId';
import { useOrgRole } from '@/hooks/useOrgRole';
import MoneyActionHint from '@/components/billing/MoneyActionHint';
import { formatUtcDeadline } from '@/utils/formatUtcDeadline';
import { formatDate, useStrings } from '@/i18n';
import { formatNumber } from '@/i18n/format';
import { fmtDate } from '@/i18n/fmtDate';
import { formatPriceCents } from '@/constants/billing-display';
import {
  billingStrings,
  type BillingTextKey,
} from '@/i18n/dictionaries/billing';
import { localizeError } from '@/utils/localizeError';
import { billingApi, type UsageDebtStatus } from '@/api/billing';
import { minorUnitsToDisplayAmount } from '@/utils/zeroDecimalCurrencies';
import { formatRevenueMoney } from '@/utils/revenueMetric';
import type { CheckoutPeriod, PlanTerms, TrialOffer } from '@/types/billing';
import { usePlanView, type PlanView } from './planView';
import PlanTermsModal from './PlanTermsModal';

/**
 * WEB-3302 юнит «тариф» — тариф организации на ките по эталону рескина
 * (`design/cabinet-rescin/sections/org.jsx`: карточка `planCard` стр. 851–862,
 * плашки `alert` 871–874, «Начните пробный период» `noneBlock` 888–898,
 * раскладка 1008–1016). Два узла одного файла: `PlanNotices` — над сеткой
 * (плашки и «Начните пробный период»), `CurrentPlanCard` — карточка в сетке
 * `.ob-top` рядом с кошельком. Что рисовать, решает только `planView` (вид из
 * `accessState` ответа сервера, `planView.ts`).
 *
 * Деньги (R4): сумм кабинет не считает. «Управление подпиской», «Обновить
 * карту» — прежний портал (`createPortalThunk`, условие `stripeCustomerId`,
 * WEB-593).
 *
 * WEB-4008 (возврат после WEB-3302; ответы владельца 29.10 №1–4, ответ сессии
 * 52 29.10 ~18:35). Все суммы и остатки — из ответа `GET /billing`
 * (`planTerms`, `trialProgress`), кабинет только форматирует
 * (`formatPriceCents`, `formatNumber`); исключение — доля полосы пробного из
 * двух чисел ответа. `GET /billing/plans` карточка не читает. Цена печатается
 * только там, где следующее действие клиента — оформление (`PRICE_VIEWS` и
 * карточка «Начните пробный период»), у оплаченной подписки её нет: сумма плана
 * — не «следующее списание» (цена живой подписки — WEB-4027). «Оплатить …» и
 * «Начать пробный период» оформляют напрямую (`createCheckoutThunk` с периодом
 * карточки), окна выбора тарифа здесь нет: тариф один, сервер цену берёт из
 * плана организации по периоду. WEB-4180: вместо переключателя периода — две
 * карточки периода со своей кнопкой (`TrialPick`, общий узел с окном
 * `TrialAsk`), пробный обещается только при признаке сервера `trialOffer:
 * 'offered'`; плашка «не оплачено» — две кнопки, без `planTerms` одна
 * «Оплатить тариф» помесячно. Отказ — `localizeError` (фразы реестра
 * кодов ru/en, как у `PricingModal.handleUpgrade`); «подписка уже есть» —
 * ещё и перечитывает `GET /billing`; 403 (нет права на деньги) — своя фраза
 * `planCheckoutForbidden`, текст сервера не показывается.
 */

/** Виды карточки, где следующее действие — оформление (ответ владельца 29.10 №1). */
const PRICE_VIEWS: ReadonlySet<PlanView> = new Set<PlanView>([
  'trial',
  'unpaid',
  'debt',
]);

/** Условия тарифа из ответа сервера (WEB-4008); `null` — нет или скрыты маской. */
export function usePlanTerms(): PlanTerms | null {
  return useAppSelector((state) => state.billing?.planTerms ?? null);
}

/** Правило пробного: дни и переходы — `planTerms.trial` ответа. */
function trialRule(s: typeof billingStrings.ru, terms: PlanTerms): string {
  const { days, exitLimit } = terms.trial;
  return s.planNoneRule(days, exitLimit, formatNumber(exitLimit));
}

/**
 * WEB-4180: признак «пробный положен» из ответа `GET /billing` (одна функция
 * сервера с оформлением); `null` — нет в ответе, пробный не обещаем.
 */
export function useTrialOffer(): TrialOffer | null {
  return useAppSelector((state) => state.billing?.trialOffer ?? null);
}

/** Сумма из ответа — только форматирование, валюта — из того же ответа. */
export function money(terms: PlanTerms, cents: number): string {
  return formatPriceCents(cents, terms.currency);
}

/**
 * Право оформить тариф — то же правило, что у «Пополнить» (`useOrgTopUp`
 * `canSettle`): роль неизвестна — показываем (настоящего владельца не прячем),
 * иначе — `canManageBilling` организации (сервер судит `isProjectOrgManager`).
 */
export function useCanSettle(): boolean {
  const { role, canManageOrgBilling } = useOrgRole();
  return role === null || canManageOrgBilling;
}

/**
 * Прямое оформление тарифа с периодом. Повторное нажатие, пока идёт запрос,
 * ничего не шлёт; удалось — переход на адрес оплаты (кнопка остаётся
 * погашенной, страница уходит).
 */
export function usePlanCheckout(showToast: (text: string, kind: 'err') => void) {
  const s = useStrings(billingStrings);
  const dispatch = useAppDispatch();
  const projectId = useBillingProjectId();
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const pay = async (period: CheckoutPeriod) => {
    if (inFlight.current) return;
    if (!projectId) {
      showToast(s.noProjectsCheckoutUnavailable, 'err');
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      // Тариф один (WEB-2503): сервер поле tier не читает, цену берёт из плана
      // организации по периоду.
      const result = await dispatch(
        createCheckoutThunk({ projectId, tier: 'PRO', period }),
      ).unwrap();
      window.location.href = result.url;
    } catch (err: unknown) {
      // 403 — сервер отказал по праву на деньги голым английским текстом без
      // кода реестра: показываем свою фразу словаря, текст сервера — никогда.
      const status =
        err && typeof err === 'object' && 'status' in err
          ? (err as { status?: unknown }).status
          : undefined;
      showToast(
        status === 403
          ? s.planCheckoutForbidden
          : localizeError(err, s.checkoutFailed),
        'err',
      );
      const code =
        err && typeof err === 'object' && 'code' in err
          ? (err as { code?: unknown }).code
          : undefined;
      if (code === 'CHECKOUT_SUBSCRIPTION_EXISTS') {
        void dispatch(fetchBillingThunk(undefined));
      }
      inFlight.current = false;
      setBusy(false);
    }
  };
  return { projectId, busy, pay };
}

interface TrialPickProps {
  readonly terms: PlanTerms;
  /** Признак сервера: пробный обещаем только при `offered`. */
  readonly offer: TrialOffer | null;
  /** Оформление с периодом карточки (`usePlanCheckout().pay`). */
  readonly onStart: (period: CheckoutPeriod) => void;
  readonly busy: boolean;
  readonly projectId: string | null | undefined;
  /** `bal-period` на странице, `trial-ask-pick` в окне. */
  readonly testid?: string;
}

/**
 * WEB-4180 — две карточки периода со своей кнопкой (эталон рескина c9ab75a2,
 * `TrialPick`): первая — месяц (главная кнопка), вторая — год
 * (второстепенная), выбора по умолчанию нет. Суммы — только из `planTerms`
 * ответа: месяц — `monthlyChargeCents`, в месяц за год — `annualMonthlyCents`,
 * за год сразу — `annualChargeCents`, экономия — `annualSavingsCents`; год из
 * месяца не считается. Пробный не положен (`used_by_you`, `used_by_org`) или
 * признака нет — кнопки «Оплатить <сумма>» и под каждой своя строка
 * немедленного списания (третий ответ сессии 52, п. 1). Общий узел блока
 * «до пробного» и окна `TrialAsk`.
 */
export function TrialPick({
  terms,
  offer,
  onStart,
  busy,
  projectId,
  testid = 'bal-period',
}: TrialPickProps) {
  const s = useStrings(billingStrings);
  const t = terms.tariff;
  const trial = offer === 'offered';
  const month = money(terms, t.monthlyChargeCents);
  const year = money(terms, t.annualChargeCents);
  const cards = [
    {
      period: 'month' as const,
      label: s.planPickMonthLabel,
      price: s.planPickPerMonth(month),
      note: s.planPickMonthNote,
      charge: month,
      kind: 'primary' as const,
    },
    {
      period: 'year' as const,
      label: s.planPickYearLabel,
      price: s.planPickPerMonth(money(terms, t.annualMonthlyCents)),
      note: s.planPickYearNote(
        year,
        t.annualSavingsCents > 0 ? money(terms, t.annualSavingsCents) : null,
      ),
      charge: year,
      kind: 'secondary' as const,
    },
  ];
  return (
    <div className="bx-pk" data-testid={testid}>
      {cards.map((c) => (
        <div key={c.period} className="bx-pkc" data-testid={`bal-period-${c.period}`}>
          <span className="t">{c.label}</span>
          <b>{c.price}</b>
          <span className="hint">{c.note}</span>
          {/* WEB-1648: адреса проекта нет → кнопка называет причину. */}
          <MoneyActionHint
            hasProjectId={!!projectId}
            wrapTestId={`bal-start-${c.period}-wrap`}
            block
          >
            <Button
              kind={c.kind}
              data-testid={`bal-start-${c.period}`}
              onClick={() => onStart(c.period)}
              loading={busy}
              disabled={!projectId}
            >
              {trial ? s.planNoneStart : s.planPayNow(c.charge)}
            </Button>
          </MoneyActionHint>
          {!trial && (
            <span className="hint" data-testid={`bal-charge-${c.period}`}>
              {s.planChargeNow(c.charge)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * WEB-4180 — заголовок, подсказка и строка под заголовком блока «до
 * пробного» и окна `TrialAsk` по признаку сервера: `offered` — «Начните
 * пробный период» и строка раннего конца (`earlyLine`, граница переходов из
 * `planTerms`); `used_by_you` / `used_by_org` — «Оформить тариф» и строка
 * своей ветки (тексты утверждены владельцем 03.11); признака нет —
 * «Оформить тариф» без строки и без подсказки.
 */
export function trialOfferCopy(
  s: typeof billingStrings.ru,
  offer: TrialOffer | null,
  terms: PlanTerms | null,
): { title: string; help: string | null; line: string | null } {
  if (offer === 'offered') {
    return {
      title: s.planNoneTitle,
      help: terms
        ? s.planNoneHelp(
            trialRule(s, terms),
            money(terms, terms.tariff.monthlyChargeCents),
            money(terms, terms.tariff.annualMonthlyCents),
          )
        : null,
      line: terms
        ? s.planTrialEarlyEnd(
            terms.trial.exitLimit,
            formatNumber(terms.trial.exitLimit),
          )
        : null,
    };
  }
  if (offer === 'used_by_you') {
    return { title: s.planGetTitle, help: s.planUsedHelpYou, line: s.planUsedByYou };
  }
  if (offer === 'used_by_org') {
    return { title: s.planGetTitle, help: s.planUsedHelpOrg, line: s.planUsedByOrg };
  }
  return { title: s.planGetTitle, help: null, line: null };
}

/**
 * WEB-4180 — кнопки плашки «Тариф не оплачен» (эталон c9ab75a2 `payBox`):
 * «Оплатить <месяц> за месяц» (главная, `bal-pay-month`) и «Оплатить <год> за
 * год» (`bal-pay`), переключателя периода нет. Без `planTerms` суммы нет —
 * одна кнопка «Оплатить тариф» помесячно. Без права на деньги — «Исправит
 * владелец».
 */
function PlanPayButtons({
  showToast,
}: {
  readonly showToast: (text: string, kind: 'err') => void;
}) {
  const s = useStrings(billingStrings);
  const terms = usePlanTerms();
  const canSettle = useCanSettle();
  const { projectId, busy, pay } = usePlanCheckout(showToast);

  if (!canSettle) {
    return (
      <span className="hint" data-testid="bal-owner-fix">
        {s.planOwnerFixes}
      </span>
    );
  }
  const t = terms?.tariff ?? null;
  return (
    // WEB-1648: адреса проекта нет → кнопки называют причину.
    <MoneyActionHint hasProjectId={!!projectId} wrapTestId="bal-pay-wrap">
      <span className="bx-pay">
        <Button
          kind="primary"
          size="sm"
          data-testid="bal-pay-month"
          onClick={() => void pay('month')}
          loading={busy}
          disabled={!projectId}
        >
          {terms && t
            ? s.planPayMonth(money(terms, t.monthlyChargeCents))
            : s.planPay(null)}
        </Button>
        {terms && t && (
          <Button
            kind="secondary"
            size="sm"
            data-testid="bal-pay"
            onClick={() => void pay('year')}
            loading={busy}
            disabled={!projectId}
          >
            {s.planPayYear(money(terms, t.annualChargeCents))}
          </Button>
        )}
      </span>
    </MoneyActionHint>
  );
}

/** Точка статуса кита по виду (эталон `planStatus`, стр. 849). */
const STATUS_KIND: Partial<Record<PlanView, StatusKind>> = {
  active: 'live',
  trial: 'idle',
  unpaid: 'off',
  debt: 'off',
  pastdue: 'bad',
};

/** Текст статуса по виду (эталон `st.*`). */
const STATUS_TEXT: Partial<Record<PlanView, BillingTextKey>> = {
  active: 'planStatusActive',
  trial: 'planStatusTrial',
  unpaid: 'planStatusNotPaid',
  debt: 'planStatusNotPaid',
  pastdue: 'planStatusPaymentFailed',
};

/**
 * WEB-3302 юнит «условия» — кнопка «Тарифы и условия» видна, пока сервер не
 * спрятал платёжную часть маской (`billingHidden`, решение сессии 52 30.10).
 * Судится по признаку, а не по `planTerms === null`: `null` бывает и у
 * владельца без службы плана — тогда кнопка есть, окно пишет «недоступно».
 */
export function useShowPlanTerms(): boolean {
  return !useAppSelector((state) => state.billing?.billingHidden ?? false);
}

/** Валюта долга по объёму: в ответе `GET /billing/usage-debt` её нет — счета платформы в долларах (ответ сессии 52 30.10). */
const USAGE_DEBT_CURRENCY = 'usd';

/** Сумма долга по объёму: наименьшие единицы → сумма валюты → печать. */
function usageDebtAmount(cents: number): string {
  return formatRevenueMoney(
    minorUnitsToDisplayAmount(cents, USAGE_DEBT_CURRENCY),
    USAGE_DEBT_CURRENCY,
  );
}

/**
 * WEB-3302 юнит «условия» — плашка долга по объёму (вид `debt`, эталон
 * org.jsx:845) с «Оплатить долг». Сводка — `GET /billing/usage-debt`, оплата —
 * `POST /billing/usage-debt/pay` (клиент WEB-3298, право — `canManageBilling`,
 * иначе 403 до Stripe): без права нет ни кнопки, ни запроса. Исходы оплаты
 * (ответ сессии 52, 30.10 ~03:25): `paid` — перечитать сводку и `GET /billing`;
 * `nothing_due` — «долга нет» и перечитать; `action_required` — страница счёта
 * Stripe (неуспешная карта и подтверждение банка); `not_paid` — счёт
 * аннулирован или черновик, всплывашка «напишите в поддержку». Повторное
 * нажатие, пока идёт запрос, ничего не шлёт.
 */
function UsageDebtNotice({
  showToast,
}: {
  readonly showToast: (text: string, kind: 'err' | 'ok') => void;
}) {
  const s = useStrings(billingStrings);
  const dispatch = useAppDispatch();
  const projectId = useBillingProjectId();
  const canSettle = useCanSettle();
  const [status, setStatus] = useState<UsageDebtStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    if (!projectId || !canSettle) {
      setStatus(null);
      return;
    }
    try {
      const res = await billingApi.getUsageDebt(projectId);
      if (mine === seq.current) setStatus(res.data);
    } catch {
      // сводки нет — плашка без суммы, кнопка остаётся
      if (mine === seq.current) setStatus(null);
    }
  }, [projectId, canSettle]);

  useEffect(() => {
    void load();
  }, [load]);

  const reread = () => {
    void load();
    void dispatch(fetchBillingThunk(undefined));
  };

  const pay = async () => {
    if (inFlight.current || !projectId) return;
    inFlight.current = true;
    setBusy(true);
    let leaving = false;
    try {
      const { data } = await billingApi.payUsageDebt(projectId);
      if (data.outcome === 'action_required') {
        if (data.hostedInvoiceUrl) {
          leaving = true;
          window.location.href = data.hostedInvoiceUrl;
        } else {
          showToast(s.usageDebtPayFailed, 'err');
        }
      } else if (data.outcome === 'not_paid') {
        showToast(s.usageDebtNotPayable, 'err');
      } else {
        if (data.outcome === 'nothing_due') showToast(s.usageDebtNothingDue, 'ok');
        reread();
      }
    } catch (err: unknown) {
      showToast(localizeError(err, s.usageDebtPayFailed), 'err');
    } finally {
      if (!leaving) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };

  const amount =
    status && status.amountDueCents > 0
      ? usageDebtAmount(status.amountDueCents)
      : null;

  return (
    <Notice
      kind="error"
      title={s.debtT(amount)}
      testid="bal-usage-debt"
      action={
        canSettle ? (
          // WEB-1648: адреса проекта нет → кнопка называет причину.
          <MoneyActionHint hasProjectId={!!projectId} wrapTestId="bal-debt-wrap">
            <Button
              kind="primary"
              size="sm"
              data-testid="bal-debt"
              onClick={() => void pay()}
              loading={busy}
              disabled={!projectId}
            >
              {s.debtBtn}
            </Button>
          </MoneyActionHint>
        ) : null
      }
    >
      {s.debtD}
    </Notice>
  );
}

/**
 * Портал Stripe по проекту организации (WEB-1524: выбранный проект либо мост —
 * первый проект активной организации). Отказ — всплывашка кита.
 */
function usePortal(showToast: (text: string, kind: 'err') => void) {
  const s = useStrings(billingStrings);
  const dispatch = useAppDispatch();
  const projectId = useBillingProjectId();
  const open = async () => {
    if (!projectId) {
      // WEB-1524: недостижимо через UI (кнопки погашены без projectId);
      // возможно только в организации без единого проекта.
      showToast(s.noProjectsBillingUnavailable, 'err');
      return;
    }
    try {
      const result = await dispatch(createPortalThunk(projectId)).unwrap();
      window.location.href = result.url;
    } catch (err: unknown) {
      showToast(localizeError(err, s.portalOpenFailed), 'err');
    }
  };
  return { projectId, open };
}

/**
 * WEB-4180 — блок «до пробного» (`bal-none`, эталон c9ab75a2 `noneBlock`):
 * заголовок с подсказкой и строка под ним по признаку сервера
 * (`trialOfferCopy`), две карточки периода со своей кнопкой (`TrialPick`),
 * «Тарифы и условия». Без права на деньги вместо карточек — «Тарифом и оплатой
 * распоряжается владелец организации».
 */
function PlanNoneBlock({
  showToast,
  showTerms,
  onOpenTerms,
}: {
  readonly showToast: (text: string, kind: 'err') => void;
  readonly showTerms: boolean;
  readonly onOpenTerms: () => void;
}) {
  const s = useStrings(billingStrings);
  const terms = usePlanTerms();
  const offer = useTrialOffer();
  const canSettle = useCanSettle();
  const { projectId, busy, pay } = usePlanCheckout(showToast);
  const copy = canSettle
    ? trialOfferCopy(s, offer, terms)
    : { title: s.planNoneTitle, help: null, line: s.planOwnerOnly };
  return (
    <div className="card bx-none" data-testid="bal-none">
      <div className="bx-nt">
        <b data-testid="bal-none-title">
          {copy.title}
          {copy.help && <Help tip={copy.help} />}
        </b>
        {copy.line && (
          <span className="hint" data-testid="bal-none-line">
            {copy.line}
          </span>
        )}
      </div>
      {canSettle && terms && (
        <TrialPick
          terms={terms}
          offer={offer}
          busy={busy}
          projectId={projectId}
          onStart={(period) => void pay(period)}
        />
      )}
      {showTerms && (
        <div>
          <Button kind="ghost" data-testid="bal-plans-none" onClick={onOpenTerms}>
            {s.prTitle}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Над сеткой: «Тариф не оплачен» (`unpaid`), «Оплата не прошла» (`pastdue`),
 * «оплачено с баланса» (поведение WEB-1094, вид `Notice` кита) и «Начните
 * пробный период» (`none`). Вид `hidden` (без доступа к деньгам) плашек не
 * видит: сервер не отдал ни тарифа, ни подписки.
 */
export function PlanNotices() {
  const s = useStrings(billingStrings);
  const view = usePlanView();
  const terms = usePlanTerms();
  const showTerms = useShowPlanTerms();
  const [termsOpen, setTermsOpen] = useState(false);
  const [toast, showToast] = useToast();
  const { projectId, open } = usePortal(showToast);
  const subscription = useAppSelector(
    (state) => state.billing?.subscription ?? null,
  );
  // WEB-1599 (подтема 2): момент отключения в явно названном поясе; null —
  // момента нет или он неразбираем, тогда прежний текст без выдуманной даты.
  const graceDeadline = formatUtcDeadline(subscription?.publicGraceUntil);
  // WEB-1487×WEB-1524: снимок подписки может принадлежать организации, с
  // которой уже ушли (BillingState.subscriptionOrgId) — гасит только плашку
  // просрочки.
  const subscriptionOrgId = useAppSelector(
    (state) => state.billing?.subscriptionOrgId ?? null,
  );
  const activeOrgId = useAppSelector(
    (state) => state.organizations?.activeOrgId ?? null,
  );
  const isSubscriptionStaleForOrg =
    subscriptionOrgId !== null &&
    activeOrgId !== null &&
    subscriptionOrgId !== activeOrgId;
  // WEB-1094 (деньги): персистентная плашка — держится, пока карту не поправят.
  const paidFromBalanceBanner = useAppSelector(
    (state) => state.billing?.paidFromBalanceBanner ?? false,
  );
  const accessState = useAppSelector(
    (state) => state.billing?.accessState ?? null,
  );

  if (view === 'hidden') return null;
  // WEB-4180: «Пробный период закончился» — по состоянию доступа, вид тот же.
  const trialEnded = accessState === 'trial_ended';

  return (
    <>
      {view === 'unpaid' && (
        <Notice
          kind="error"
          title={
            <>
              {trialEnded ? s.planEndedTitle : s.planUnpaidTitle}
              {/* WEB-4180: выгода годовой — в «?» у заголовка (эталон payTip). */}
              {terms && terms.tariff.annualSavingsCents > 0 && (
                <Help
                  tip={s.planSaveLine(
                    money(terms, terms.tariff.annualMonthlyCents),
                    money(terms, terms.tariff.annualSavingsCents),
                  )}
                />
              )}
            </>
          }
          testid="bal-unpaid"
          action={<PlanPayButtons showToast={showToast} />}
        >
          {trialEnded ? s.planEndedText : s.planUnpaidText}
        </Notice>
      )}
      {view === 'debt' && <UsageDebtNotice showToast={showToast} />}
      {view === 'pastdue' && !isSubscriptionStaleForOrg && (
        <Notice
          kind="error"
          title={s.pastDueTitle}
          testid="bal-pastdue"
          action={
            // WEB-1648: адреса проекта нет → кнопка называет причину.
            <MoneyActionHint
              hasProjectId={!!projectId}
              wrapTestId="bal-card-wrap"
            >
              <Button
                kind="primary"
                size="sm"
                data-testid="bal-card"
                onClick={open}
                disabled={!projectId}
              >
                {s.updateCard}
              </Button>
            </MoneyActionHint>
          }
        >
          {graceDeadline
            ? s.planPastDueUntil(graceDeadline)
            : s.pastDueNoDeadline}
        </Notice>
      )}
      {paidFromBalanceBanner && (
        <Notice
          kind="info"
          title={s.paidFromBalanceTitle}
          testid="paid-from-balance-banner"
          action={
            <MoneyActionHint
              hasProjectId={!!projectId}
              wrapTestId="paid-from-balance-portal-wrap"
            >
              <Button
                size="sm"
                data-testid="paid-from-balance-portal"
                onClick={open}
                disabled={!projectId}
              >
                {s.updateCard}
              </Button>
            </MoneyActionHint>
          }
        >
          {s.paidFromBalanceText}
        </Notice>
      )}
      {view === 'none' && (
        <PlanNoneBlock
          showToast={showToast}
          showTerms={showTerms}
          onOpenTerms={() => setTermsOpen(true)}
        />
      )}
      {/* WEB-3302 юнит «условия»: окно одно на странице — здесь только у вида
          none (у остальных его ставит карточка тарифа); вне bal-none, чтобы
          числа окна не попадали в текст карточки; ставится только открытым. */}
      {view === 'none' && showTerms && termsOpen && (
        <PlanTermsModal open onClose={() => setTermsOpen(false)} />
      )}
      {toast}
    </>
  );
}

/**
 * Карточка тарифа в сетке `.ob-top` (вида `none` у неё нет — вместо неё
 * «Начните пробный период» над сеткой).
 */
export default function CurrentPlanCard() {
  const s = useStrings(billingStrings);
  const view = usePlanView();
  const [toast, showToast] = useToast();
  const { projectId, open } = usePortal(showToast);
  const tier = useAppSelector((state) => state.billing?.tier ?? 'UNKNOWN');
  const subscription = useAppSelector(
    (state) => state.billing?.subscription ?? null,
  );
  const isLoadingCheckout = useAppSelector(
    (state) => state.billing?.isLoadingCheckout ?? false,
  );
  // QA-возврат 25.08: явный признак сервера «платёжная часть скрыта
  // настройками доступа» (types/billing.ts BillingResponse.billingHidden).
  const billingHidden = useAppSelector(
    (state) => state.billing?.billingHidden ?? false,
  );
  const planTerms = usePlanTerms();
  const showTerms = useShowPlanTerms();
  const [termsOpen, setTermsOpen] = useState(false);
  const trialProgress = useAppSelector(
    (state) => state.billing?.trialProgress ?? null,
  );

  const label = (
    <span className="k">
      {s.planLabel}
      <Help tip={s.planHelp} />
    </span>
  );

  // WEB-1290: сервер не отдал тариф читателю без доступа к деньгам — ни цены,
  // ни статуса не угадываем. Причина называется, только когда сервер её
  // подтвердил (billingHidden).
  if (view === 'hidden') {
    return (
      <div className="card ob-col" data-testid="bal-plan">
        <div className="stat">{label}</div>
        <span className="hint">{s.planOwnerOnly}</span>
        {billingHidden && (
          <span className="hint" data-testid="bal-plan-hidden-reason">
            {s.planHiddenReason}
          </span>
        )}
      </div>
    );
  }
  if (view === 'none') return null;

  const status = subscription?.status ?? 'ACTIVE';
  // WEB-593: условие портала — stripeCustomerId (его проверяет сервер).
  const canManageSubscription = Boolean(subscription?.stripeCustomerId);
  const periodEnd = subscription?.currentPeriodEnd
    ? formatDate(subscription.currentPeriodEnd, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  // WEB-4125: счётчика дней на карточке нет — срок пробного рисует только блок
  // «Пробный период» (PlanTrialBlock, trialProgress.daysLeft), эталон
  // org.jsx:828. Дата конца — как у эталона (dfmt: месяц словом, год текущего
  // года не пишется).
  const trialEnd = trialProgress?.endsAt
    ? fmtDate(trialProgress.endsAt, { year: 'auto' })
    : null;

  // WEB-4008 (ответ владельца 29.10 №1): цена — только у видов, где следующее
  // действие — оформление; у оплаченной подписки (active, pastdue) её нет.
  // Источник — planTerms ответа GET /billing; /billing/plans карточка не зовёт.
  const price =
    planTerms && PRICE_VIEWS.has(view)
      ? money(planTerms, planTerms.tariff.monthlyChargeCents)
      : null;

  // Прежняя строка дат (WEB-665, WEB-1080). Сняты: ветка «нет подписки —
  // бесплатный тариф» и строка у видов «не оплачено» и «долг» — там дата
  // прошедшего периода читалась бы как «Продлится …» (возврат 1).
  const renewalText = (() => {
    if (view === 'unpaid' || view === 'debt') return null;
    // WEB-4008: конец пробного — с сервера (досрочный конец учтён там).
    // WEB-4125 (эталон org.jsx:828): «Пробный до {дата}, дальше {цена}/мес»;
    // цены нет (planTerms null или под маской) — без «дальше».
    if (view === 'trial' && trialProgress) {
      if (!trialEnd) return null;
      return price
        ? s.planTrialUntilWithPrice(trialEnd, price)
        : s.planTrialUntil(trialEnd);
    }
    if (status === 'TRIALING' && periodEnd) {
      return s.planTrialUntil(periodEnd);
    }
    if (subscription?.cancelAtPeriodEnd && periodEnd)
      return s.planCancelsOn(periodEnd);
    if (periodEnd) return s.planRenewsOn(periodEnd);
    if (tier === 'FREE') return null;
    return s.planGrantedManually;
  })();

  const statusKind = STATUS_KIND[view];
  const statusText = STATUS_TEXT[view];

  const card = (
    <div className="card ob-col" data-testid="bal-plan">
      <div className="stat">
        {label}
        {price && (
          <span className="v" data-testid="bal-plan-price">
            {price}
            <span className="ob-pm">{s.perMonthShort}</span>
          </span>
        )}
      </div>
      {(statusKind || renewalText) && (
        <div className="stack">
          {statusKind && statusText && (
            <Status kind={statusKind}>{s[statusText] as string}</Status>
          )}
          {renewalText && (
            <span className="hint" data-testid="bal-plan-dates">
              {renewalText}
            </span>
          )}
        </div>
      )}
      {(canManageSubscription || showTerms) && (
        <div className="stack">
          {canManageSubscription && (
            // WEB-1648: адреса проекта нет → кнопка называет причину.
            <MoneyActionHint
              hasProjectId={!!projectId}
              wrapTestId="bal-manage-wrap"
            >
              <Button
                size="sm"
                data-testid="bal-manage"
                onClick={open}
                loading={isLoadingCheckout}
                disabled={!projectId}
              >
                {s.manageSubscription}
              </Button>
            </MoneyActionHint>
          )}
          {/* WEB-3302 юнит «условия»: «Тарифы и условия» (эталон org.jsx:860) */}
          {showTerms && (
            <Button
              size="sm"
              kind="ghost"
              data-testid="bal-plans"
              onClick={() => setTermsOpen(true)}
            >
              {s.prTitle}
            </Button>
          )}
        </div>
      )}
      {toast}
    </div>
  );

  // Окно — рядом с карточкой, не внутри: числа окна не входят в текст карточки.
  // Ставится только открытым (onClose приходит после затухания): закрытое окно
  // не лежит третьим узлом в сетке .ob-top рядом с кошельком и карточкой.
  return showTerms && termsOpen ? (
    <>
      {card}
      <PlanTermsModal open onClose={() => setTermsOpen(false)} />
    </>
  ) : (
    card
  );
}

/**
 * WEB-4008 — блок «Пробный период» (`bal-trial`, эталон org.jsx:849–856,
 * условие 21): только у вида «пробный» и только когда сервер прислал ход
 * пробного (`trialProgress`). Остатки (`daysLeft`, `exitsLeft`) и
 * израсходованное (`exitsUsed`) — числа ответа; кабинет считает лишь долю
 * полосы из двух чисел ответа. Полоса переходов предупреждающим цветом не
 * красится: порога нет (ответ владельца 29.10 №2).
 */
export function PlanTrialBlock() {
  const s = useStrings(billingStrings);
  const view = usePlanView();
  const terms = usePlanTerms();
  const progress = useAppSelector(
    (state) => state.billing?.trialProgress ?? null,
  );
  if (view !== 'trial' || !progress || !terms) return null;
  const { days, exitLimit } = terms.trial;

  return (
    <section className="sec" data-testid="bal-trial">
      <h2>
        {s.planStatusTrial}
        <Help tip={trialRule(s, terms)} />
      </h2>
      <div className="card ob-col">
        <div className="ob-tr">
          {progress.daysLeft !== null && (
            <div className="ob-tr-i" data-testid="bal-trial-days">
              <div className="stack ob-tr-h">
                <span className="hint">{s.trialDaysLabel}</span>
                <b className="num">{s.trialDaysLeftTag(progress.daysLeft)}</b>
              </div>
              <Bar
                value={days - progress.daysLeft}
                max={days}
                size="md"
                width="100%"
                label={s.trialDaysLabel}
              />
            </div>
          )}
          <div className="ob-tr-i" data-testid="bal-trial-exits">
            <div className="stack ob-tr-h">
              <span className="hint">{s.trialExitsLabel}</span>
              <b className="num">
                {s.trialExitsLeft(
                  progress.exitsLeft,
                  formatNumber(progress.exitsLeft),
                )}
              </b>
            </div>
            <Bar
              value={progress.exitsUsed}
              max={exitLimit}
              size="md"
              width="100%"
              label={s.trialExitsLabel}
            />
            <span className="hint num">
              {s.trialOf(formatNumber(progress.exitsUsed), formatNumber(exitLimit))}
              <i className="vsep" />
              {s.trialThenPaid}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
